import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    KeyRound, AlertTriangle, CheckCircle2, XCircle,
    Eye, EyeOff, Shield, Activity, Info, Search
} from 'lucide-react'

type TabType = 'scan' | 'env' | 'docs'
type Severity = 'safe' | 'warning' | 'exposed'

interface SecretScan {
    pattern: string
    file: string
    line: number
    severity: Severity
    desc: string
}

const SCANS: SecretScan[] = [
    { pattern: 'SUPABASE_URL', file: '.env', line: 1, severity: 'safe', desc: 'Variable de entorno — no expuesta en bundle' },
    { pattern: 'SUPABASE_ANON_KEY', file: '.env', line: 2, severity: 'safe', desc: 'Anon key (público) — protegido por RLS' },
    { pattern: 'VITE_SUPABASE_URL', file: 'src/lib/supabase.ts', line: 3, severity: 'safe', desc: 'Inyectado por Vite — solo URL pública' },
    { pattern: 'VITE_SUPABASE_ANON_KEY', file: 'src/lib/supabase.ts', line: 4, severity: 'safe', desc: 'Anon key — público por diseño de Supabase' },
    { pattern: 'SERVICE_ROLE_KEY', file: 'N/A', line: 0, severity: 'safe', desc: 'No presente en código frontend ✅' },
    { pattern: 'API_SECRET', file: 'N/A', line: 0, severity: 'safe', desc: 'No detectado en codebase' },
    { pattern: 'hardcoded JWT', file: 'N/A', line: 0, severity: 'safe', desc: 'No detectados tokens JWT hardcodeados' },
    { pattern: 'console.log(secret)', file: 'N/A', line: 0, severity: 'safe', desc: 'No se loguean secrets en console' },
]

const ENV_VARS = [
    { name: 'VITE_SUPABASE_URL', type: 'public', value: 'https://***.supabase.co', risk: 'low' },
    { name: 'VITE_SUPABASE_ANON_KEY', type: 'public', value: 'eyJhbGci...', risk: 'low' },
    { name: 'SUPABASE_SERVICE_ROLE_KEY', type: 'private', value: '█████████', risk: 'high' },
    { name: 'SUPABASE_DB_PASSWORD', type: 'private', value: '█████████', risk: 'critical' },
]

export default function SecretsPage() {
    const [tab, setTab] = useState<TabType>('scan')

    const safeCount = SCANS.filter(s => s.severity === 'safe').length
    const warnCount = SCANS.filter(s => s.severity === 'warning').length
    const expCount = SCANS.filter(s => s.severity === 'exposed').length

    const sevStyles: Record<Severity, { icon: React.ReactNode; color: string }> = {
        safe: { icon: <CheckCircle2 size={14} />, color: 'text-neon-green' },
        warning: { icon: <AlertTriangle size={14} />, color: 'text-yellow-400' },
        exposed: { icon: <XCircle size={14} />, color: 'text-red-400' },
    }

    const tabs = [
        { id: 'scan' as const, label: 'Scan', icon: <Search size={16} /> },
        { id: 'env' as const, label: 'Variables', icon: <KeyRound size={16} /> },
        { id: 'docs' as const, label: 'Best Practices', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <KeyRound className="text-neon-blue" /> Secrets Scanner
                </h1>
                <p className="text-gray-500 text-sm">Detección de variables sensibles y secrets expuestos</p>
            </div>

            <FeatureNotEnabled moduleName="Secrets Scanner" />

            {/* Score */}
            <Card className={`mb-6 ${expCount === 0 ? 'bg-neon-green/5 border border-neon-green/20' : 'bg-red-500/5 border border-red-500/20'}`}>
                <div className="flex items-center gap-4">
                    {expCount === 0 ? <Shield size={24} className="text-neon-green" /> : <AlertTriangle size={24} className="text-red-400" />}
                    <div>
                        <span className={`font-bold text-sm ${expCount === 0 ? 'text-neon-green' : 'text-red-400'}`}>
                            {expCount === 0 ? '✅ No se detectaron secrets expuestos' : `⚠️ ${expCount} secret(s) expuesto(s)`}
                        </span>
                        <p className="text-gray-500 text-xs mt-0.5">{safeCount} safe · {warnCount} warnings · {expCount} exposed</p>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                    { label: 'Safe', value: safeCount, color: 'text-neon-green' },
                    { label: 'Warnings', value: warnCount, color: 'text-yellow-400' },
                    { label: 'Exposed', value: expCount, color: 'text-red-400' },
                ].map((s, i) => (
                    <Card key={i} className="!p-4 text-center">
                        <div className="text-2xl font-bold font-mono text-white">{s.value}</div>
                        <div className={`text-[10px] uppercase tracking-wider mt-1 ${s.color}`}>{s.label}</div>
                    </Card>
                ))}
            </div>

            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'scan' && (
                <div className="space-y-2">
                    {SCANS.map((s, i) => {
                        const sv = sevStyles[s.severity]
                        return (
                            <Card key={i} className="!p-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={sv.color}>{sv.icon}</div>
                                        <div>
                                            <code className="text-neon-blue text-xs font-mono">{s.pattern}</code>
                                            {s.file !== 'N/A' && <span className="text-gray-600 text-[10px] ml-2">{s.file}:{s.line}</span>}
                                            <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                                        </div>
                                    </div>
                                    <span className={`text-[10px] font-mono ${sv.color}`}>{s.severity.toUpperCase()}</span>
                                </div>
                            </Card>
                        )
                    })}
                </div>
            )}

            {tab === 'env' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><KeyRound size={16} className="text-neon-blue" /> Environment Variables</h2>
                    <div className="space-y-2">
                        {ENV_VARS.map(v => (
                            <div key={v.name} className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {v.type === 'public' ? <Eye size={14} className="text-blue-400" /> : <EyeOff size={14} className="text-red-400" />}
                                    <div>
                                        <code className="text-white text-xs font-mono">{v.name}</code>
                                        <p className="text-gray-600 text-xs font-mono mt-0.5">{v.value}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${v.type === 'public' ? 'bg-blue-500/10 text-blue-400' : 'bg-red-500/10 text-red-400'}`}>{v.type}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${v.risk === 'low' ? 'bg-neon-green/10 text-neon-green' : v.risk === 'high' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-red-500/10 text-red-400'}`}>{v.risk}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'docs' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-3">🔒 Reglas de Seguridad</h2>
                        <div className="space-y-2">
                            {[
                                { rule: 'Nunca hardcodear secrets', desc: 'Usar variables de entorno (.env) — nunca en código fuente' },
                                { rule: '.env en .gitignore', desc: 'Archivo .env NUNCA se sube a Git — solo .env.example' },
                                { rule: 'VITE_ prefix = público', desc: 'Variables con VITE_ se inyectan en el bundle — solo para datos públicos' },
                                { rule: 'Service Role solo en backend', desc: 'SUPABASE_SERVICE_ROLE_KEY solo en Edge Functions o servidor' },
                                { rule: 'Rotar keys periódicamente', desc: 'Rotar anon/service keys cada 90 días como mínimo' },
                            ].map(r => (
                                <div key={r.rule} className="p-3 bg-black/20 rounded-lg">
                                    <span className="text-white text-sm font-medium">{r.rule}</span>
                                    <p className="text-gray-500 text-xs mt-0.5">{r.desc}</p>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    )
}
